import type { PorExceptionResponseEnvelope } from "@credit-acceptance/step6-bff-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { workbenchStipulationsApi } from "../../../api/client";
import { patchStipulationListsForApproval } from "./patchStipulationLists";
import { stipulationKeys } from "./stipulationKeys";
import type { StipulationListView } from "./types";

export interface PorExceptionInput {
  approvalId: string;
  applicantPk?: number;
  checked: boolean;
}

/**
 * Patches every cached stipulation list row for this applicant with the
 * committed `exceptionGranted` state, so the checkbox reflects the DB value
 * on the next render without waiting for a refetch.
 * @param {StipulationListView} current - Cached list to patch.
 * @param {number} applicantPk - The applicant whose POR exception was toggled.
 * @param {boolean} exceptionGranted - The committed state from the response envelope.
 * @returns {StipulationListView} The patched list.
 */
function patchListForPorException(
  current: StipulationListView,
  applicantPk: number,
  exceptionGranted: boolean
): StipulationListView {
  return {
    ...current,
    items: current.items.map((item) =>
      item.applicantPk === applicantPk ? { ...item, porExceptionInd: exceptionGranted } : item
    ),
  };
}

/**
 * Mutation hook for setting POR exception (secondary proofs) on a stipulation's applicant.
 * @returns {ReturnType<typeof useMutation<PorExceptionResponseEnvelope, Error, PorExceptionInput>>} The TanStack mutation result.
 */
export function usePorExceptionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: PorExceptionInput): Promise<PorExceptionResponseEnvelope> => {
      if (!input.applicantPk) {
        throw new Error("applicantPk is required for POR exception");
      }
      const response = await workbenchStipulationsApi.updatePorException(input.approvalId, input.applicantPk, {
        exceptionGranted: input.checked,
      });
      return response.data;
    },
    onSuccess: (response, input) => {
      const { applicantPk } = input;
      const { exceptionGranted } = response;
      if (!applicantPk || exceptionGranted === undefined) {
        return;
      }

      patchStipulationListsForApproval(queryClient, input.approvalId, (data) =>
        patchListForPorException(data, applicantPk, exceptionGranted)
      );

      // The PUT response only carries the committed flag, not the row's
      // recomputed availableActions/reasons (stipulation-service's PRE_APP_GR
      // reasons branch on this same flag — see
      // StipulationPanelAssemblyService#toAvailableAction). Without this, the
      // proof-type submenu keeps showing the pre-toggle reason set until
      // something else refetches the list. Invalidate so the next GET picks
      // up the reasons the panel-assembly service now computes off the
      // already-committed flag.
      void queryClient.invalidateQueries({ queryKey: stipulationKeys.listsForApproval(input.approvalId) });
    },
  });
}
